import { axiosReq } from "@/lib/axios";
import { queryClient } from "@/lib/react-query" 
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import posthog from "posthog-js";
import { useMemo } from "react";
import { toast } from "sonner";

export type ImageBody = {
  image_url: string,
  catalogue_item: string,
  id: number,
  prompt: string,
  wishlist: boolean,
  post_id: string[]
}

export type CatalogImageBody = {
  catalogue: string,
  images: ImageBody[]
}

export const useGetGallery = (open: boolean) => {

  const request = async (pageParam: number): Promise<{
    data: CatalogImageBody[]
  }> => {
    const response = await axiosReq.post('/api/landing/gallery/', {
      page: pageParam
    })
    return response.data
  }

  const { data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status, } = useInfiniteQuery({
      queryKey: ["gallery"],
      queryFn: ({ pageParam = 1 }) => request(pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        const hasImages = lastPage.data.some(item => item.images.length > 0);
        if (!hasImages) return undefined;
        return allPages.length + 1;
      },
      enabled: open,
    });

  const gallery = useMemo(() => {
    if (!data) return [];

    const catalogueMap = new Map<string, ImageBody[]>();

    data.pages.forEach(page => {
      page.data.forEach(item => {
        const existingImages = catalogueMap.get(item.catalogue) || [];
        catalogueMap.set(item.catalogue, [...existingImages, ...item.images]);
      });
    });

    return Array.from(catalogueMap.entries()).map(([catalogue, images]) => ({
      catalogue,
      images
    }));
  }, [data]);

  return {
    gallery,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status
  }
}

type ImageUploadArgs = {
  catalogue: string,
  user: string,
  media_file: File,
  prompt: string
}

export const useUploadPostImage = () => {

  const request = async (data: ImageUploadArgs): Promise<{
    image_path: string,
    image_id: number
  }> => {

    const config = {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    };

    const response = await axiosReq.post('/api/approval/upload-post-media/', data, config);
    return response.data;
  };

  const { mutate: uploadImage, data, isSuccess, isPending } = useMutation({
    mutationFn: request,
    onSuccess: (data) => {
      posthog.capture('image uploaded by user', { image: data.image_id })
      toast.success("Image uploaded successfully");
      return data;
    },
    onError: (error) => {
      toast.error("Failed to upload image");
      console.error("Image upload error:", error);
    }
  });

  return { uploadImage, isSuccess, data, isPending };
};


type UpdatePostBodyArgs = {
  post_id: number;
  post_content: string;
  hashtags: string[];
  post_date: string,
  post_time: string,
  catalogue: string
}

export const useUpdatePostBody = () => {

  const request = async (postBody: UpdatePostBodyArgs) => {
    const response = await axiosReq.post('/api/approval/post-save/', postBody)
    return response.data;
  }
  const { mutate: updatePostBody, isSuccess } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["socials"] });
      queryClient.invalidateQueries({ queryKey: ["schedule_posts"] })
      queryClient.invalidateQueries({ queryKey: ["socials-marked"] })
      queryClient.invalidateQueries({ queryKey: ["socials-unreviewed"] })
      toast.success("Content updated")
    }
  })

  return { updatePostBody, isSuccess }
}

export const useSaveImage = () => {
  const request = async (data: { post_id: number, image_id: number }) => {
    const response = await axiosReq.post('/api/approval/save-image-post/', data);
    return response.data
  }

  const { mutate: saveImage, status } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["socials"] });
      queryClient.invalidateQueries({ queryKey: ["schedule_posts"] })
      queryClient.invalidateQueries({ queryKey: ["socials-marked"] })
      queryClient.invalidateQueries({ queryKey: ["socials-unreviewed"] })
      queryClient.invalidateQueries({ queryKey: ["themes"] })
      queryClient.invalidateQueries({ queryKey: ["theme-details"] })
    }
  })

  return { saveImage, status }

}

export const useGetWishlist = (open: boolean) => {

  const request = async (pageParam: number): Promise<{
    data: ImageBody[]
  }> => {
    const response = await axiosReq.post('/api/landing/wishlist/', {
      page: pageParam
    })
    return response.data
  }

  const { data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status, } = useInfiniteQuery({
      queryKey: ["wishlist"],
      queryFn: ({ pageParam = 1 }) => request(pageParam),
      initialPageParam: 1,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.data.length === 0) return undefined;
        return allPages.length + 1;
      },
      enabled: open,
    });

  let wishlist = data?.pages.flatMap(page => page.data)

  return {
    wishlist,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status
  }
}


export const useGetVideoGallery = (open: boolean) => {
  const request = async (pageParam: number): Promise<{
    data: CatalogImageBody;
  }> => {
    const response = await axiosReq.post('/api/landing/gallery-catalogue/', {
      page: pageParam,
      catalogue: "video"
    });
    return response.data;
  };

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: ["video-catalogue"],
    queryFn: ({ pageParam = 1 }) => request(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const hasImages = lastPage.data?.images.length > 0;
      if (!hasImages) return undefined;
      return allPages.length + 1;
    },
    enabled: open // fetch only if open is true and catalogue is provided
  });

  const gallery = useMemo(() => {
    if (!data) return [];

    // Since only one catalogue is returned, flatten all pages' images into a single array
    const allImages: ImageBody[] = data.pages.flatMap(page => page.data.images);
    console.log(allImages)

    return allImages;
  }, [data]);

  return {
    gallery,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  };
};


// Custom hook to add wishlist image
export const useWishlistImage = () => {

  const request = async (id: number): Promise<{ message: string }> => {
    const response = await axiosReq.post('/api/landing/set-wishlist/', { id });
    return response.data
  }

  const { mutate: wishlistImage, status } = useMutation({
    mutationFn: request,
    onSuccess: (data) => {
      if (data) {

        posthog.capture('image wishlisted')
        queryClient.invalidateQueries({ queryKey: ["wishlist"] })
      }
    }
  })

  return { wishlistImage, status }

}
